import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import AnimatedCheck from '../components/AnimatedCheck';
import AnimatedError from '../components/AnimatedError';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, KeyRound, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { extractApiErrorMessage, networkErrorMessage } from '../lib/errors';
import { validatePassword, validateConfirmPassword } from '../lib/validators';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SetPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [touched, setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Token verification state: 'loading' | 'valid' | 'already_set' | 'expired' | 'invalid'
  const [tokenState, setTokenState] = useState(token ? 'loading' : 'invalid');
  const [tokenEmail, setTokenEmail] = useState('');

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/signup/verify-set-password-token?token=${encodeURIComponent(token)}`);
        const data = await res.json();
        if (cancelled) return;
        setTokenState(data.status || 'invalid');
        if (data.email) setTokenEmail(data.email);
      } catch {
        if (!cancelled) setTokenState('invalid');
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const errors = useMemo(() => ({
    password: validatePassword(password),
    confirmPassword: validateConfirmPassword(password, confirmPassword),
  }), [password, confirmPassword]);

  const isValid = !errors.password && !errors.confirmPassword;
  const markTouched = (f) => setTouched((t) => ({ ...t, [f]: true }));
  const showError = (f) => touched[f] && errors[f];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ password: true, confirmPassword: true });
    if (!isValid) {
      toast.error('Corrija os campos destacados antes de continuar.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/signup/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setSuccess(true);
          toast.success('Senha definida com sucesso!');
        } else {
          toast.error('Não foi possível definir a senha. Tente novamente.');
        }
      } else {
        toast.error(await extractApiErrorMessage(res, 'Erro ao definir senha'));
      }
    } catch (error) {
      toast.error(networkErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  // ----- States: loading / already_set / expired / invalid / success -----

  if (tokenState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center" data-testid="set-password-loading">
            <Loader2 className="w-8 h-8 animate-spin text-teal-500 mx-auto mb-3" />
            <p className="text-muted-foreground">Verificando link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (tokenState === 'already_set') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full" data-testid="set-password-already-set">
          <CardContent className="pt-6 text-center">
            <AnimatedCheck />
            <h2 className="text-2xl font-bold mb-2">Sua senha já foi configurada</h2>
            <p className="text-muted-foreground mb-6">
              {tokenEmail
                ? <>A conta <strong>{tokenEmail}</strong> já está pronta para uso.</>
                : <>Sua conta já está pronta para uso.</>}
              <br />Faça login para acessar a plataforma.
            </p>
            <Button onClick={() => navigate('/login')} data-testid="set-password-go-login">
              Fazer login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (tokenState === 'expired') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full" data-testid="set-password-expired">
          <CardContent className="pt-6 text-center">
            <AnimatedError variant="warning" />
            <h2 className="text-2xl font-bold mb-2">Link expirado</h2>
            <p className="text-muted-foreground mb-6">
              Este link de definição de senha expirou.
              Use a opção <strong>"Esqueci minha senha"</strong> para gerar um novo.
            </p>
            <Button onClick={() => navigate('/login', { state: { openForgot: true, email: tokenEmail } })} data-testid="set-password-forgot">
              Esqueci minha senha
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (tokenState === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full" data-testid="set-password-invalid">
          <CardContent className="pt-6 text-center">
            <AnimatedError />
            <h2 className="text-2xl font-bold mb-2">Link inválido</h2>
            <p className="text-muted-foreground mb-6">
              Este link de definição de senha não é mais válido.
            </p>
            <Button onClick={() => navigate('/')} data-testid="set-password-back">
              Voltar
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AnimatedCheck />
            <h2 className="text-2xl font-bold mb-2">Senha Definida!</h2>
            <p className="text-muted-foreground mb-6">
              Sua senha foi salva com sucesso. Agora você pode fazer login.
            </p>
            <Button onClick={() => navigate('/login')} data-testid="set-password-login">
              Ir para Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // tokenState === 'valid' → render the form
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-teal-100">
              <KeyRound className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <CardTitle>Definir Senha</CardTitle>
              <CardDescription>Crie uma senha para acessar sua conta.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="password">Nova senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => markTouched('password')}
                  placeholder="Mínimo 8 caracteres, com letra e número"
                  aria-invalid={!!showError('password')}
                  className={(showError('password') ? 'border-red-500 focus-visible:ring-red-500 ' : '') + 'pr-10'}
                  data-testid="set-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {showError('password') && (
                <p className="text-xs text-red-600" data-testid="set-password-error">{errors.password}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirm">Confirmar senha</Label>
              <div className="relative">
                <Input
                  id="confirm"
                  type={showConfirm ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onBlur={() => markTouched('confirmPassword')}
                  placeholder="Digite a senha novamente"
                  aria-invalid={!!showError('confirmPassword')}
                  className={(showError('confirmPassword') ? 'border-red-500 focus-visible:ring-red-500 ' : '') + 'pr-10'}
                  data-testid="set-password-confirm"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                  aria-label={showConfirm ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {showError('confirmPassword') && (
                <p className="text-xs text-red-600" data-testid="set-password-confirm-error">{errors.confirmPassword}</p>
              )}
              {!showError('confirmPassword') && confirmPassword && password === confirmPassword && (
                <p className="text-xs text-teal-600 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> Senhas conferem
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={submitting || !isValid}
              data-testid="set-password-submit"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salvar Senha
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
